import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { shipmentService } from '../../services/shipment.service';

const ShipmentTrackingMap = ({ selectedShipmentId, shipmentData }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const routesLayerRef = useRef(null);
  const telemetryLayerRef = useRef(null);
  const allMarkersRef = useRef([]);
  
  // Add state for telemetry data and display toggle
  const [telemetryData, setTelemetryData] = useState([]);
  const [showTelemetry, setShowTelemetry] = useState(false);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  
  // Add state for route data and bounds
  const [routeData, setRouteData] = useState(null);
  const [bounds, setBounds] = useState([]);

  // Add state for temperature alert
  const [temperatureAlert, setTemperatureAlert] = useState(false);
  const [currentTemperature, setCurrentTemperature] = useState(null);

  // Define neutral colors
  const colors = {
    primary: '#2196f3',    // Blue - normal status
    secondary: '#4caf50',  // Green - good status
    accent: '#ff9800',     // Orange - warning
    alert: '#f44336',      // Red - alert/error
    telemetry: '#9c27b0'   // Purple - for telemetry path
  };

  // Define temperature threshold constants
  const TEMPERATURE_THRESHOLDS = {
    MIN: -20, // Minimum acceptable temperature (°C)
    MAX: -10  // Maximum acceptable temperature (°C)
  };

  // Listen for telemetry toggle events from ShipmentDetails
  useEffect(() => {
    const handleTelemetryToggle = (event) => {
      setShowTelemetry(event.detail.showTelemetry);
      
      if (event.detail.showTelemetry && selectedShipmentId && telemetryData.length === 0) {
        fetchTelemetryData(selectedShipmentId);
      } else if (event.detail.showTelemetry && telemetryData.length > 0) {
        displayTelemetryData();
      } else {
        hideTelemetryData();
      }
    };

    window.addEventListener('telemetryToggle', handleTelemetryToggle);
    
    return () => {
      window.removeEventListener('telemetryToggle', handleTelemetryToggle);
    };
  }, [selectedShipmentId, telemetryData]);

  // Fetch telemetry data
  const fetchTelemetryData = async (shipmentId) => {
    try {
      setTelemetryLoading(true);
      
      // Using shipmentService instead of direct axios call
      const response = await shipmentService.getShipmentTelemetry(shipmentId);
      
      console.log("Telemetry data response:", response); // Debug log
      
      // Check if the response is directly the telemetry data
      if (response) {
        setTelemetryData(response);
        if (showTelemetry) {
          displayTelemetryData(response);
        }
      } else {
        console.warn('No telemetry data available');
        setTelemetryData(null);
      }
      
      setTelemetryLoading(false);
    } catch (err) {
      console.error('Error fetching telemetry data:', err);
      setTelemetryLoading(false);
    }
  };

  // Display telemetry data on the map
  const displayTelemetryData = (data = telemetryData) => {
    try {
      const L = window.L;
      if (!L || !mapInstanceRef.current || !data || data.length === 0) return;
      
      // Clear existing telemetry layer if it exists
      if (telemetryLayerRef.current) {
        telemetryLayerRef.current.clearLayers();
      } else {
        telemetryLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      }
      
      // Create points for the telemetry path
      const points = data.map(point => [
        parseFloat(point.latitude), 
        parseFloat(point.longitude)
      ]);
      
      // Create a polyline for the telemetry path
      const telemetryPath = L.polyline(points, {
        color: colors.telemetry,
        weight: 3,
        opacity: 0.7,
        dashArray: '5, 10',
        lineJoin: 'round'
      }).addTo(telemetryLayerRef.current);
      
      // Add markers for each telemetry point
      data.forEach(point => {
        // Ensure latitude and longitude are numbers
        const lat = parseFloat(point.latitude);
        const lng = parseFloat(point.longitude);
        
        // Skip invalid coordinates
        if (isNaN(lat) || isNaN(lng)) {
          console.warn('Invalid coordinates in telemetry data:', point);
          return;
        }
        
        // Determine marker color based on temperature
        let markerColor;
        
        if (point.temperature === null || point.temperature === undefined) {
          // No temperature data available - use blue
          markerColor = '#2196f3'; // Blue
        } else if (point.temperature < TEMPERATURE_THRESHOLDS.MIN || point.temperature > TEMPERATURE_THRESHOLDS.MAX) {
          // Temperature out of range - use red
          markerColor = '#f44336'; // Red
        } else {
          // Temperature within range - use green
          markerColor = '#4caf50'; // Green
        }
        
        // Override color if status is ALERT
        if (point.status === 'ALERT') {
          markerColor = '#f44336'; // Red for alerts
        }
        
        // Format timestamp
        const timestamp = new Date(point.timestamp).toLocaleString();
        
        // Create marker
        const marker = L.circleMarker([lat, lng], {
          radius: 6,
          fillColor: markerColor,
          color: '#fff',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        }).addTo(telemetryLayerRef.current);
        
        // Create detailed popup content
        const popupContent = document.createElement('div');
        popupContent.className = 'telemetry-popup';
        popupContent.innerHTML = `
          <div style="min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; border-bottom: 1px solid #eee; padding-bottom: 5px;">
              Telemetry Data
            </h4>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 3px 0; font-weight: bold;">Time:</td>
                <td style="padding: 3px 0;">${timestamp}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-weight: bold;">Status:</td>
                <td style="padding: 3px 0;">
                  <span style="
                    display: inline-block;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 12px;
                    background-color: ${point.status === 'ALERT' ? '#f44336' : 
                                       point.status === 'STOPPED' ? '#ff9800' : 
                                       point.status === 'ARRIVED' ? '#4caf50' : '#2196f3'};
                    color: white;
                  ">${point.status || 'IN_TRANSIT'}</span>
                </td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-weight: bold;">Temperature:</td>
                <td style="padding: 3px 0;">
                  ${point.temperature !== null && point.temperature !== undefined ? 
                    `<span style="
                      display: inline-block;
                      padding: 2px 6px;
                      border-radius: 4px;
                      font-size: 12px;
                      background-color: ${
                        point.temperature < TEMPERATURE_THRESHOLDS.MIN || point.temperature > TEMPERATURE_THRESHOLDS.MAX ? 
                        '#f44336' : '#4caf50'
                      };
                      color: white;
                    ">${point.temperature}°C</span>
                    ${
                      point.temperature < TEMPERATURE_THRESHOLDS.MIN || point.temperature > TEMPERATURE_THRESHOLDS.MAX ?
                      `<span style="color: #f44336; font-size: 12px; margin-left: 5px;">
                        (Outside ${TEMPERATURE_THRESHOLDS.MIN}°C - ${TEMPERATURE_THRESHOLDS.MAX}°C range)
                      </span>` : ''
                    }` : 
                    'N/A'
                  }
                </td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-weight: bold;">Humidity:</td>
                <td style="padding: 3px 0;">${point.humidity !== null && point.humidity !== undefined ? `${point.humidity}%` : 'N/A'}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-weight: bold;">Location:</td>
                <td style="padding: 3px 0;">${lat.toFixed(6)}, ${lng.toFixed(6)}</td>
              </tr>
            </table>
            ${point.additional_data ? `
              <h4 style="margin: 10px 0 5px 0; border-bottom: 1px solid #eee; padding-bottom: 5px;">
                Additional Data
              </h4>
              <table style="width: 100%; border-collapse: collapse;">
                ${Object.entries(point.additional_data).map(([key, value]) => `
                  <tr>
                    <td style="padding: 3px 0; font-weight: bold;">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</td>
                    <td style="padding: 3px 0;">${
                      typeof value === 'boolean' 
                        ? (value ? '✓' : '✗') 
                        : (key === 'battery' ? `${value}%` : value)
                    }</td>
                  </tr>
                `).join('')}
              </table>
            ` : ''}
            ${point.status === 'ALERT' && point.additional_data?.alert_type ? `
              <div style="margin-top: 10px; padding: 8px; background-color: #ffebee; border-radius: 4px;">
                <strong>Alert:</strong> ${point.additional_data.alert_type.replace(/_/g, ' ')}
              </div>
            ` : ''}
            ${point.status === 'STOPPED' && point.additional_data?.stop_reason ? `
              <div style="margin-top: 10px; padding: 8px; background-color: #fff8e1; border-radius: 4px;">
                <strong>Stop Reason:</strong> ${point.additional_data.stop_reason.replace(/_/g, ' ')}
                ${point.additional_data.maintenance_note ? `<br><strong>Note:</strong> ${point.additional_data.maintenance_note}` : ''}
              </div>
            ` : ''}
          </div>
        `;
        
        // Attach popup to marker
        marker.bindPopup(popupContent, {
          maxWidth: 300,
          minWidth: 200,
          autoPan: true,
          closeButton: true,
          autoClose: false
        });
        
        // Add hover effect
        marker.on('mouseover', function() {
          this.setStyle({
            radius: 8,
            fillOpacity: 1
          });
        });
        
        marker.on('mouseout', function() {
          this.setStyle({
            radius: 6,
            fillOpacity: 0.8
          });
        });
        
        // Add click handler
        marker.on('click', function() {
          this.openPopup();
        });
      });
      
      // Fit the map to the telemetry path if there are points
      if (points.length > 0) {
        mapInstanceRef.current.fitBounds(telemetryPath.getBounds(), {
          padding: [50, 50],
          maxZoom: 12
        });
      }
    } catch (error) {
      console.error('Error displaying telemetry data:', error);
    }
  };
  
  // Hide telemetry data
  const hideTelemetryData = () => {
    if (telemetryLayerRef.current) {
      telemetryLayerRef.current.clearLayers();
    }
    
    // Reset view to original route
    if (allMarkersRef.current.length > 0) {
      const bounds = window.L.latLngBounds(allMarkersRef.current);
      mapInstanceRef.current.fitBounds(bounds, {
        padding: [50, 50]
      });
    }
  };

  useEffect(() => {
    // Load Leaflet dynamically to avoid SSR issues
    const loadLeaflet = async () => {
      try {
        // Only load if not already loaded
        if (!window.L) {
          // Load Leaflet CSS
          const linkEl = document.createElement('link');
          linkEl.rel = 'stylesheet';
          linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(linkEl);
          
          // Load Leaflet JS
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }
        
        initializeMap();
      } catch (err) {
        console.error('Error loading Leaflet:', err);
        setError('Failed to load map resources. Please refresh the page.');
        setLoading(false);
      }
    };
    
    loadLeaflet();
    
    return () => {
      // Clean up map instance when component unmounts
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update map when selected shipment changes
  useEffect(() => {
    if (mapInstanceRef.current && selectedShipmentId) {
      fetchRouteData(selectedShipmentId);
      
      // Fetch telemetry data if showing telemetry
      if (showTelemetry) {
        fetchTelemetryData(selectedShipmentId);
      }
    } else if (mapInstanceRef.current) {
      // Clear the map if no shipment is selected
      clearMap();
    }
  }, [selectedShipmentId]);

  // Initialize the map
  const initializeMap = () => {
    try {
      if (!mapRef.current) return;
      
      // Create map if it doesn't exist
      if (!mapInstanceRef.current) {
        const L = window.L;
        
        // Create map
        const map = L.map(mapRef.current, {
          center: [39.8283, -98.5795], // Center of US
          zoom: 4
        });
        
        mapInstanceRef.current = map;
        
        // Add tile layer
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);
        
        // Create layers for markers and routes
        markersLayerRef.current = L.layerGroup().addTo(map);
        routesLayerRef.current = L.layerGroup().addTo(map);
        
        setLoading(false);
      }
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map. Please refresh the page.');
      setLoading(false);
    }
  };

  // Clear all markers and routes from the map
  const clearMap = () => {
    if (markersLayerRef.current) {
      markersLayerRef.current.clearLayers();
    }
    if (routesLayerRef.current) {
      routesLayerRef.current.clearLayers();
    }
    if (telemetryLayerRef.current) {
      telemetryLayerRef.current.clearLayers();
    }
    allMarkersRef.current = [];
    setTelemetryData([]);
  };

  // Fetch GeoJSON data for the selected shipment
  const fetchRouteData = async (shipmentId) => {
    if (!shipmentId) {
      setRouteData(null);
      setTelemetryData(null);
      setBounds([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Using shipmentService instead of direct axios call
      const response = await shipmentService.getShipmentRoute(shipmentId);
      
      console.log("Route data response:", response); // Debug log
      
      // Check if the response has route data
      if (response && response.route && response.route.length > 0) {
        // Get the first and last points from the route
        const firstPoint = response.route[0];
        const lastPoint = response.route[response.route.length - 1];
        
        // Find the current point (either a point with status "in_transit" or the middle point)
        let currentPoint = response.route.find(point => point.status === 'in_transit');
        if (!currentPoint) {
          // If no in_transit point, use the middle point
          currentPoint = response.route[Math.floor(response.route.length / 2)];
        }
        
        // Create a more complete routeData object
        const enhancedRouteData = {
          ...response,
          // Use first point as origin
          origin: {
            lat: firstPoint.lat,
            lng: firstPoint.lng,
            name: response.origin?.name || 'Origin',
            address: 'Starting Location',
            status: firstPoint.status,
            time: firstPoint.time || response.departure_time
          },
          // Use last point as destination
          destination: {
            lat: lastPoint.lat,
            lng: lastPoint.lng,
            name: response.destination?.name || 'Destination',
            address: 'Final Destination',
            status: lastPoint.status,
            time: lastPoint.time || response.estimated_arrival
          },
          // Use current point as current location
          current_location: {
            lat: currentPoint.lat,
            lng: currentPoint.lng,
            name: 'Current Location',
            status: currentPoint.status || response.status,
            timestamp: currentPoint.time || new Date().toISOString()
          },
          // Include temperature data if available
          temperature: response.temperature,
          temperature_range: response.temperature_range,
          vehicle_type: response.vehicle_type,
          carrier: response.carrier,
          tracking_number: response.tracking_number
        };
        
        console.log("Enhanced route data:", enhancedRouteData);
        setRouteData(enhancedRouteData);
        
        // Calculate bounds for the map
        const latLngs = response.route.map(point => [point.lat, point.lng]);
        if (latLngs.length > 0) {
          setBounds(latLngs);
        }
      } else {
        throw new Error('Failed to load route data or no route points available');
      }
      
      // Fetch telemetry data if needed
      if (showTelemetry) {
        await fetchTelemetryData(shipmentId);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching route data:', err);
      setError('Failed to load map data');
      setLoading(false);
    }
  };

  // Update map with GeoJSON data
  const updateMapWithGeoJson = (routeData) => {
    try {
      const L = window.L;
      if (!L || !mapInstanceRef.current || !routeData) return;
      
      // Clear existing markers and routes
      clearMap();
      
      console.log("Updating map with route data:", routeData);
      
      // Extract route points
      const routePoints = routeData.route || [];
      if (routePoints.length === 0) {
        setError('No route data available for this shipment');
        return;
      }
      
      // Create route line
      const coordinates = routePoints.map(point => [point.lat, point.lng]);
      const routeLine = L.polyline(coordinates, {
        color: colors.primary,
        weight: 3,
        opacity: 0.7
      }).addTo(routesLayerRef.current);
      
      // Add origin marker
      if (routeData.origin) {
        console.log("Adding origin marker at:", [routeData.origin.lat, routeData.origin.lng]);
        
        // Create a warehouse icon for origin
        const warehouseSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="${colors.secondary}">
            <path d="M0 0h24v24H0z" fill="none"/>
            <path d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z"/>
          </svg>
        `;
        
        const originMarker = L.marker([routeData.origin.lat, routeData.origin.lng], {
          icon: L.divIcon({
            className: 'custom-marker',
            html: `<div style="filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.5));">${warehouseSvg}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          })
        });
        
        // Add popup with origin information
        originMarker.bindPopup(`
          <div>
            <h4>Origin</h4>
            <p>Name: ${routeData.origin.name || 'Warehouse'}</p>
            <p>Status: ${routeData.origin.status || 'departed'}</p>
            <p>Time: ${new Date(routeData.origin.time || routeData.departure_time || '').toLocaleString()}</p>
          </div>
        `);
        
        // Add to layer
        originMarker.addTo(markersLayerRef.current);
        
        // Store marker for bounds calculation
        allMarkersRef.current.push([routeData.origin.lat, routeData.origin.lng]);
      }
      
      // Add destination marker
      if (routeData.destination) {
        console.log("Adding destination marker at:", [routeData.destination.lat, routeData.destination.lng]);
        
        // Create a destination icon
        const destinationSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="${colors.accent}">
            <path d="M0 0h24v24H0z" fill="none"/>
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        `;
        
        const destinationMarker = L.marker([routeData.destination.lat, routeData.destination.lng], {
          icon: L.divIcon({
            className: 'custom-marker',
            html: `<div style="filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.5));">${destinationSvg}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          })
        });
        
        // Add popup with destination information
        destinationMarker.bindPopup(`
          <div>
            <h4>Destination</h4>
            <p>Name: ${routeData.destination.name || 'Final Destination'}</p>
            <p>Status: ${routeData.destination.status || 'estimated_arrival'}</p>
            <p>Time: ${new Date(routeData.destination.time || routeData.estimated_arrival || '').toLocaleString()}</p>
          </div>
        `);
        
        // Add to layer
        destinationMarker.addTo(markersLayerRef.current);
        
        // Store marker for bounds calculation
        allMarkersRef.current.push([routeData.destination.lat, routeData.destination.lng]);
      }
      
      // Add current location marker
      // For simplicity, we'll use the middle point of the route if current_location is not provided
      const currentLocation = routeData.current_location || {
        lat: routePoints[Math.floor(routePoints.length / 2)].lat,
        lng: routePoints[Math.floor(routePoints.length / 2)].lng,
        status: routeData.status || 'IN_TRANSIT'
      };
      
      console.log("Adding current location marker at:", [currentLocation.lat, currentLocation.lng]);
      console.log("Route data for popup:", {
        carrier: routeData.carrier,
        tracking: routeData.tracking_number,
        vehicle: routeData.vehicle_type,
        temp: routeData.temperature
      });
      
      // Determine truck color based on status and temperature
      let truckColor = colors.primary; // Default blue
      let statusInfo = '';
      
      // Check for temperature alerts if temperature data exists
      if (routeData.temperature !== undefined) {
        const temp = routeData.temperature;
        // Use temperature range from shipment data if available, otherwise use default range
        const tempRange = routeData.temperature_range || { min: 2, max: 8 };
        
        if (temp < tempRange.min || temp > tempRange.max) {
          truckColor = colors.alert; // Red for temperature out of range
          statusInfo = `
            <p style="color: ${colors.alert}; font-weight: bold;">
              Temperature Alert: ${temp}°C 
              (Outside ${tempRange.min}°C - ${tempRange.max}°C range)
            </p>
          `;
        } else {
          truckColor = colors.secondary; // Green for normal temperature
          statusInfo = `<p>Temperature: ${temp}°C (Normal)</p>`;
        }
      }
      // Check shipment status
      else if (routeData.status === 'DELAYED' || currentLocation.status === 'DELAYED') {
        truckColor = colors.accent; // Orange for delays
        statusInfo = `<p style="color: ${colors.accent}; font-weight: bold;">Status: Delayed</p>`;
      } else if (routeData.status === 'DELIVERED' || currentLocation.status === 'DELIVERED') {
        truckColor = colors.secondary; // Green for delivered
        statusInfo = `<p style="color: ${colors.secondary};">Status: Delivered</p>`;
      } else if (routeData.status === 'ALERT' || currentLocation.status === 'ALERT') {
        truckColor = colors.alert; // Red for alerts
        statusInfo = `<p style="color: ${colors.alert}; font-weight: bold;">Alert: Issue detected</p>`;
      }
      
      // Create a truck icon for current location with the determined color
      const truckSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30" fill="${truckColor}">
          <path d="M0 0h24v24H0z" fill="none"/>
          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
        </svg>
      `;
      
      const currentLocationMarker = L.marker([currentLocation.lat, currentLocation.lng], {
        icon: L.divIcon({
          className: 'custom-marker',
          html: `<div style="filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.5));">${truckSvg}</div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15]
        })
      });
      
      // Format status for display
      const displayStatus = typeof currentLocation.status === 'string' 
        ? currentLocation.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
        : (typeof routeData.status === 'string' 
            ? routeData.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
            : 'In Transit');
      
      // Add popup with vehicle and temperature information
      let vehicleInfo = '';
      if (routeData.vehicle_type) {
        vehicleInfo = `<p><strong>Vehicle:</strong> ${routeData.vehicle_type}</p>`;
      }
      
      // Create popup content with all available information
      const popupContent = `
        <div style="min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; border-bottom: 1px solid #eee; padding-bottom: 5px;">Current Location</h4>
          <p style="margin: 4px 0;"><strong>Status:</strong> ${displayStatus}</p>
          
          ${routeData.carrier ? 
            `<p style="margin: 4px 0;"><strong>Carrier:</strong> ${routeData.carrier}</p>` : 
            ''}
          
          ${routeData.tracking_number ? 
            `<p style="margin: 4px 0;"><strong>Tracking:</strong> ${routeData.tracking_number}</p>` : 
            ''}
          
          ${vehicleInfo}
          
          ${routeData.temperature !== undefined ? 
            `<p style="margin: 4px 0;"><strong>Temperature:</strong> ${routeData.temperature}°C</p>` : 
            ''}
          
          ${routeData.temperature !== undefined && routeData.temperature_range ? 
            `<p style="margin: 4px 0;"><strong>Required Range:</strong> ${routeData.temperature_range.min}°C to ${routeData.temperature_range.max}°C</p>` : 
            ''}
          
          ${statusInfo}
        </div>
      `;
      
      currentLocationMarker.bindPopup(popupContent, {
        maxWidth: 300,
        minWidth: 200
      });
      
      // Add to layer
      currentLocationMarker.addTo(markersLayerRef.current);
      
      // Store marker for bounds calculation
      allMarkersRef.current.push([currentLocation.lat, currentLocation.lng]);
      
      // Fit the map to show all markers
      if (allMarkersRef.current.length > 0) {
        const bounds = L.latLngBounds(allMarkersRef.current);
        mapInstanceRef.current.fitBounds(bounds, {
          padding: [50, 50]
        });
      }
    } catch (err) {
      console.error('Error updating map with GeoJSON:', err);
      setError('Failed to display route on map');
    }
  };

  // Now, let's update the useEffect that watches for routeData changes to call this function

  useEffect(() => {
    if (mapInstanceRef.current && routeData) {
      console.log("Route data changed, updating map:", routeData);
      updateMapWithGeoJson(routeData);
    }
  }, [routeData]);

  // Helper function to determine truck color based on temperature data
  const getTruckColor = () => {
    // If we have temperature alert, use alert color
    if (temperatureAlert) {
      return colors.alert; // Red
    }
    
    // If we have temperature data but no alert, use normal color
    if (currentTemperature !== null) {
      return colors.secondary; // Green
    }
    
    // Default color if no data available
    return colors.primary; // Blue
  };

  // Add this before the return statement
  console.log("Rendering map with routeData:", routeData);
  console.log("Origin for display:", routeData?.origin ? [routeData.origin.lat, routeData.origin.lng] : null);
  console.log("Destination for display:", routeData?.destination ? [routeData.destination.lat, routeData.destination.lng] : null);

  // Function to create the current location marker
  const createCurrentLocationMarker = (location, routeData) => {
    if (!location || !location.lat || !location.lng) return null;
    
    const L = window.L;
    if (!L) return null;
    
    // Get status for icon color
    const status = location.status || (routeData && routeData.status) || 'unknown';
    const statusLower = typeof status === 'string' ? status.toLowerCase() : 'unknown';
    
    // Determine icon color based on status and temperature
    let iconColor = '#4caf50'; // Default green
    
    if (statusLower === 'delayed' || statusLower === 'exception') {
      iconColor = '#ff9800'; // Orange for delayed
    } else if (statusLower === 'failed') {
      iconColor = '#f44336'; // Red for failed
    }
    
    // Check temperature if available
    if (routeData && routeData.temperature !== undefined && routeData.temperature_range) {
      const temp = routeData.temperature;
      const range = routeData.temperature_range;
      
      if (temp > range.max || temp < range.min) {
        iconColor = '#f44336'; // Red for temperature alert
      }
    }
    
    // Create custom truck icon
    const truckIcon = L.divIcon({
      html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" fill="${iconColor}">
        <path d="M18 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5m1.5-9H17V12h4.46L19.5 9.5M6 18.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5-1.5.67-1.5 1.5.67 1.5 1.5 1.5M20 8l3 4v5h-2c0 1.66-1.34 3-3 3s-3-1.34-3-3h-4c0 1.66-1.34 3-3 3s-3-1.34-3-3H3v-3h1V8c0-1.11.89-2 2-2h12v2h2m-6-3H6c-1.66 0-3 1.34-3 3v9h1.5c0-1.66 1.34-3 3-3s3 1.34 3 3h4c0-1.66 1.34-3 3-3s3 1.34 3 3H21V11l-3-4h-4V5Z"/>
      </svg>`,
      className: '',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });
    
    // Format status for display
    const displayStatus = typeof status === 'string' 
      ? status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
      : 'Unknown';
    
    // Create popup content with more details
    const popupContent = `
      <div style="min-width: 200px;">
        <h4 style="margin: 0 0 8px 0;">Current Location</h4>
        <p style="margin: 4px 0;"><strong>Status:</strong> ${displayStatus}</p>
        
        ${routeData && routeData.carrier ? 
          `<p style="margin: 4px 0;"><strong>Carrier:</strong> ${routeData.carrier}</p>` : 
          ''}
        
        ${routeData && routeData.tracking_number ? 
          `<p style="margin: 4px 0;"><strong>Tracking:</strong> ${routeData.tracking_number}</p>` : 
          ''}
        
        ${routeData && routeData.vehicle_type ? 
          `<p style="margin: 4px 0;"><strong>Vehicle:</strong> ${routeData.vehicle_type}</p>` : 
          ''}
        
        ${routeData && routeData.temperature !== undefined ? 
          `<p style="margin: 4px 0;"><strong>Temperature:</strong> ${routeData.temperature}°C</p>` : 
          ''}
        
        ${routeData && routeData.temperature !== undefined && routeData.temperature_range ? 
          `<p style="margin: 4px 0;"><strong>Required Range:</strong> ${routeData.temperature_range.min}°C to ${routeData.temperature_range.max}°C</p>` : 
          ''}
      </div>
    `;
    
    // Create marker with popup
    const marker = L.marker([location.lat, location.lng], { icon: truckIcon })
      .bindPopup(popupContent);
    
    return marker;
  };

  return (
    <Paper elevation={3} sx={{ height: '100%', position: 'relative' }}>
      {loading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.7)'
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading shipment tracking map...
          </Typography>
        </Box>
      )}
      
      {telemetryLoading && showTelemetry && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 10, 
            right: 10, 
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: 1,
            borderRadius: 1
          }}
        >
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="caption">
            Loading telemetry...
          </Typography>
        </Box>
      )}
      
      {error && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: 3
          }}
        >
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Box>
      )}
      
      {!selectedShipmentId && !loading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: 3
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Select a shipment to view its route
          </Typography>
        </Box>
      )}
      
      <Box 
        ref={mapRef}
        sx={{ 
          width: '100%', 
          height: '100%',
          '& .leaflet-container': { height: '100%', width: '100%' }
        }} 
      />
    </Paper>
  );
};

export default ShipmentTrackingMap; 